/**
 * Filterable table of security findings with severity badges, CVE info,
 * host/IP filter, row selection, and bulk/individual delete.
 */

import { useState, useEffect, useCallback } from 'react';
import DataTable, { type Column } from '@/components/common/DataTable';
import SeverityBadge from '@/components/common/SeverityBadge';
import { formatDate, formatCvss } from '@/utils/formatters';
import type { Finding, Severity, FindingStatus } from '@/types/finding';

interface FindingListProps {
  findings: Finding[];
  onSelect: (finding: Finding) => void;
  canDelete?: boolean;
  onDelete?: (id: string) => void;
  onBulkDelete?: (ids: string[]) => void;
  isDeleting?: boolean;
}

const UNKNOWN = 'Unknown';
const osFamily = (f: Finding) => f.host_details?.os_family ?? UNKNOWN;

const severityOptions: Severity[] = ['critical', 'high', 'medium', 'low', 'info'];
const statusOptions: FindingStatus[] = ['open', 'confirmed', 'resolved', 'false_positive', 'accepted_risk'];

export default function FindingList({
  findings,
  onSelect,
  canDelete,
  onDelete,
  onBulkDelete,
  isDeleting,
}: FindingListProps) {
  const [severityFilter, setSeverityFilter] = useState<Severity | ''>('');
  const [statusFilter, setStatusFilter] = useState<FindingStatus | ''>('');
  const [hostFilter, setHostFilter] = useState('');
  const [osFilter, setOsFilter] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Clear stale selections when the findings list changes (e.g. after delete).
  useEffect(() => {
    if (selectedIds.size === 0) return;
    const currentIds = new Set(findings.map((f) => f.id));
    setSelectedIds((prev) => {
      const cleaned = new Set([...prev].filter((id) => currentIds.has(id)));
      return cleaned.size === prev.size ? prev : cleaned;
    });
  }, [findings]);

  const families = new Set(findings.map(osFamily));
  const osOptions = [...families].filter((o) => o !== UNKNOWN).sort().concat(families.has(UNKNOWN) ? [UNKNOWN] : []);

  const filtered = findings.filter((f) => {
    if (severityFilter && f.severity !== severityFilter) return false;
    if (statusFilter && f.status !== statusFilter) return false;
    if (osFilter && osFamily(f) !== osFilter) return false;
    if (hostFilter) {
      const q = hostFilter.toLowerCase();
      const match =
        f.host?.toLowerCase().includes(q) ||
        f.host_ip?.toLowerCase().includes(q) ||
        f.asset?.label?.toLowerCase().includes(q) ||
        f.asset?.value?.toLowerCase().includes(q);
      if (!match) return false;
    }
    return true;
  });

  const allSelected = filtered.length > 0 && selectedIds.size === filtered.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < filtered.length;

  function toggleSelect(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map((f) => f.id)));
    }
  }

  function handleDelete(e: React.MouseEvent, finding: Finding) {
    e.stopPropagation();
    if (confirm(`Delete finding "${finding.title}"? This cannot be undone.`)) {
      onDelete?.(finding.id);
    }
  }

  function handleBulkDelete() {
    const count = selectedIds.size;
    if (confirm(`Delete ${count} finding${count !== 1 ? 's' : ''}? This cannot be undone.`)) {
      onBulkDelete?.([...selectedIds]);
    }
  }

  // Callback ref for indeterminate state on the select-all checkbox.
  const selectAllRef = useCallback(
    (el: HTMLInputElement | null) => {
      if (el) el.indeterminate = someSelected;
    },
    [someSelected],
  );

  const columns: Column<Finding>[] = [];

  if (canDelete) {
    columns.push({
      key: 'select',
      header: (
        <input
          ref={selectAllRef}
          type="checkbox"
          checked={allSelected}
          onChange={toggleSelectAll}
          className="h-4 w-4 rounded border-gray-300 text-indigo-600 cursor-pointer"
        />
      ),
      className: 'w-10',
      render: (item: Finding) => (
        <input
          type="checkbox"
          checked={selectedIds.has(item.id)}
          onChange={() => toggleSelect(item.id)}
          onClick={(e) => e.stopPropagation()}
          className="h-4 w-4 rounded border-gray-300 text-indigo-600 cursor-pointer"
        />
      ),
    });
  }

  columns.push(
    {
      key: 'severity',
      header: 'Severity',
      className: 'w-28',
      render: (item: Finding) => <SeverityBadge severity={item.severity} />,
    },
    {
      key: 'host',
      header: 'Host',
      render: (item: Finding) => (
        <div className="text-xs font-mono text-gray-600">
          <div>{item.host ?? item.asset?.label ?? item.asset?.value ?? '—'}</div>
          {item.host_ip && item.host_ip !== item.host && <div className="text-gray-400">{item.host_ip}</div>}
        </div>
      ),
    },
    {
      key: 'device',
      header: 'Device',
      render: (item: Finding) => {
        const d = item.host_details;
        if (!d?.os_family && !d?.device_type) return <span className="text-xs text-gray-400">{UNKNOWN}</span>;
        return (
          <div className="text-xs" title={d.os ? `${d.os}${d.os_accuracy != null ? ` (${d.os_accuracy}% match)` : ''}` : undefined}>
            <div className="font-medium text-gray-800">{d.os_family ?? UNKNOWN}</div>
            {d.device_type && <div className="text-gray-500">{d.device_type}</div>}
          </div>
        );
      },
    },
    { key: 'title', header: 'Title', sortable: true },
    {
      key: 'cve_id',
      header: 'CVE',
      render: (item: Finding) => (
        <span className="font-mono text-xs">{item.cve_id ?? '—'}</span>
      ),
    },
    {
      key: 'cvss_score',
      header: 'CVSS',
      sortable: true,
      className: 'w-20',
      render: (item: Finding) => formatCvss(item.cvss_score),
    },
    {
      key: 'status',
      header: 'Status',
      render: (item: Finding) => {
        const colors: Record<FindingStatus, string> = {
          open: 'text-red-600',
          confirmed: 'text-orange-600',
          resolved: 'text-green-600',
          false_positive: 'text-gray-500',
          accepted_risk: 'text-yellow-600',
        };
        return (
          <span className={`text-xs font-medium ${colors[item.status]}`}>
            {item.status.replace('_', ' ')}
          </span>
        );
      },
    },
    {
      key: 'scanner_engine',
      header: 'Scanner',
      className: 'w-24',
      render: (item: Finding) => (
        <span className="text-xs text-gray-500">{item.scanner_engine}</span>
      ),
    },
    {
      key: 'created_at',
      header: 'Found',
      sortable: true,
      render: (item: Finding) => <span className="text-xs">{formatDate(item.created_at)}</span>,
    },
  );

  if (canDelete) {
    columns.push({
      key: 'actions',
      header: '',
      className: 'w-16',
      render: (item: Finding) => (
        <button
          onClick={(e) => handleDelete(e, item)}
          disabled={isDeleting}
          className="rounded px-2 py-1 text-xs text-red-600 hover:bg-red-50 disabled:opacity-50"
        >
          Delete
        </button>
      ),
    });
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <select
          className="input-field w-auto"
          value={severityFilter}
          onChange={(e) => setSeverityFilter(e.target.value as Severity | '')}
        >
          <option value="">All Severities</option>
          {severityOptions.map((s) => (
            <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
          ))}
        </select>
        <select
          className="input-field w-auto"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as FindingStatus | '')}
        >
          <option value="">All Statuses</option>
          {statusOptions.map((s) => (
            <option key={s} value={s}>{s.replace('_', ' ')}</option>
          ))}
        </select>
        <select
          className="input-field w-auto"
          aria-label="Operating system"
          value={osFilter}
          onChange={(e) => setOsFilter(e.target.value)}
        >
          <option value="">All operating systems</option>
          {osOptions.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <input
          className="input-field w-48"
          type="text"
          placeholder="Filter by host / IP…"
          value={hostFilter}
          onChange={(e) => setHostFilter(e.target.value)}
        />
        <span className="self-center text-sm text-gray-500">
          {filtered.length} finding{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Bulk action bar */}
      {canDelete && selectedIds.size > 0 && (
        <div className="flex items-center gap-3 rounded-md border border-red-200 bg-red-50 px-4 py-2">
          <span className="text-sm font-medium text-red-700">
            {selectedIds.size} selected
          </span>
          <button
            onClick={handleBulkDelete}
            disabled={isDeleting}
            className="rounded-md bg-red-600 px-3 py-1 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
          >
            {isDeleting ? 'Deleting…' : 'Delete Selected'}
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="text-xs text-red-600 hover:underline"
          >
            Clear selection
          </button>
        </div>
      )}

      <DataTable
        columns={columns}
        data={filtered}
        rowKey={(item) => item.id}
        onRowClick={(item) => onSelect(item)}
        emptyMessage="No findings match the selected filters."
      />
    </div>
  );
}
